
/**
 * Module depenencies.
 */

import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { isExternalRoute } from 'src/core/utils/routes';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import GalleryNavigation from 'src/components/core/slider/gallery-navigation';
import Image from 'src/components/core/image';
import Lightbox from 'src/components/lightbox';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import Slider, { Slide } from 'src/components/core/slider';
import Type from 'src/components/core/typography';
import VideoPlayer from 'src/components/core/video-player';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';

/**
 * `Banner` type.
 */

type Banner = {
  description?: string,
  fileUrl: string,
  id: string,
  linkLabel?: string,
  linkUrl?: string,
  subtitle?: string,
  title?: string,
  videoUrl?: string
};

/**
 * `Props` type.
 */

type Props = {
  banners: Banner[],
  className?: string,
  hasPageTitle?: boolean
  isKiosk?: boolean
};

/**
 * `Section` component.
 */

const Section = styled.section<{ isKiosk?: boolean }>`
  align-items: flex-end;
  display: flex;
  height: 100vh;
  overflow: hidden;
  position: relative;
  width: 100%;

  ${ifProp('isKiosk', css`
    height: calc(100vh - ${theme('dimensions.kioskNavbarHeight')}px);
  `)}
`;

/**
 * `Gallery` styled component.
 */

const Gallery = styled.div`
  inset: 0;
  position: absolute;
`;

/**
 * `StyledSlider` styled component.
 */

const StyledSlider = styled(Slider)`
  height: 100%;

  .swiper-slide {
    height: 100%;
  }
`;

/**
 * `Overlay` styled component.
 */

const Overlay = styled.div`
  background-color: ${color.transparentize('#050404', 0.4)};
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 1;

  ${media.min('ms')`
    background-color: ${color.transparentize('#050404', 0.5)};
  `}
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(Container)`
  padding-bottom: ${units(6)};
  position: relative;
  z-index: 2;

  ${media.min('ms')`
    padding-bottom: ${units(10)};
    padding-left: 123px;
    padding-right: 123px;
  `}
`;

/**
 * `GalleryNavigationWrapper` styled component.
 */

const GalleryNavigationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${units(4)};
`;

/**
 * `StyledGalleryNavigation` styled component.
 */

const StyledGalleryNavigation = styled(GalleryNavigation)`
  && > * {
    color: ${color('white')} !important;
  }

  && > [disabled] {
    opacity: 0.4;
  }
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-row-gap: ${units(2.5)};
  grid-template-columns: 100%;

  ${media.min('ms')`
    align-items: flex-start;
    grid-column-gap: ${units(4)};
    grid-template-columns: minmax(0, ${units(73.75)}) minmax(0, ${units(58.5)});
    justify-content: space-between;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H3)`
  color: ${color('white')};
  font-size: 48px;
  letter-spacing: -0.48px;
  line-height: 1.2;
  max-width: ${units(66.5)};

  ${media.min('ms')`
    font-size: 56px;
    letter-spacing: -0.56px;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  color: ${color('white')};
  line-height: 1.5;
  margin-bottom: ${units(3)};

  ${media.min('ms')`
    margin-bottom: ${units(4)};
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  border-radius: 100px;
  color: ${color('black')};
  height: auto;
  letter-spacing: 0.14px;
  line-height: 1.5;
  min-width: 0;
  padding: ${units(2)} ${units(5)};
`;

/**
 * `SlideImage` styled component.
 */

const SlideImage = styled.div`
  cursor: pointer;
  height: 100%;
  position: relative;
`;

/**
 * `StyledVideoPlayer` styled component.
 */

const StyledVideoPlayer = styled(VideoPlayer)`
  background-color: ${color('black')};
  height: 100%;
  padding-top: 0;
  pointer-events: none;
  position: relative;

  iframe,
  video {
    aspect-ratio: 16 / 9;
    height: auto !important;
    left: 50%;
    min-height: 100%;
    min-width: 100%;
    object-fit: cover;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: auto !important;
  }
`;

/**
 * `BannerSection` component.
 */

const BannerSection = (props: Props): ReactElement | null => {
  const { banners, className, hasPageTitle, isKiosk } = props;
  const normalizedBanners = useMemo(() => {
    return filter(banners, ({ fileUrl, videoUrl }) => !isEmpty(fileUrl) || !isEmpty(videoUrl));
  }, [banners]);

  const total = size(normalizedBanners);
  const sliderRef = useRef<any>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isOpenLightbox, setOpenLightbox] = useState<boolean>();
  const activeBanner = normalizedBanners[activeSlide];
  const handleSlideChange = useCallback(({ activeIndex }) => {
    setActiveSlide(activeIndex);
  }, []);

  const handleClickDot = useCallback((index: number) => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideTo(index);
    }
  }, []);

  const handleClickSlide = useCallback(() => {
    setOpenLightbox(true);
  }, []);

  useEffect(() => {
    if (sliderRef && sliderRef.current && isOpenLightbox) {
      sliderRef.current.swiper.autoplay.stop();

      return;
    }

    if (sliderRef && sliderRef.current && !isOpenLightbox) {
      sliderRef.current.swiper.autoplay.start();

      return;
    }
  }, [isOpenLightbox]);

  if (isEmpty(normalizedBanners)) {
    return null;
  }

  return (
    <>
      <Section
        className={className}
        isKiosk={isKiosk}
      >
        <Gallery>
          <StyledSlider
            autoplay={{
              delay: 6000,
              disableOnInteraction: true
            }}
            effect={'fade'}
            onClick={handleClickSlide}
            onSlideChange={handleSlideChange}
            ref={sliderRef}
            speed={750}
          >
            {map(normalizedBanners, ({ fileUrl, id, title, videoUrl }: Banner, index: number) => (
              <Slide key={id}>
                {videoUrl ? (
                  <StyledVideoPlayer
                    controls={false}
                    loop
                    muted
                    playing={index === activeSlide}
                    playsinline
                    url={videoUrl}
                  />
                ) : (
                  <SlideImage>
                    <Image
                      alt={title ?? ''}
                      layout={'fill'}
                      objectFit={'cover'}
                      src={fileUrl}
                    />
                  </SlideImage>
                )}
              </Slide>
            ))}
          </StyledSlider>
        </Gallery>

        <Overlay />

        <Wrapper>
          {total > 1 && (
            <GalleryNavigationWrapper>
              <StyledGalleryNavigation
                current={activeSlide}
                displayPagination
                onGoToNext={() => handleClickDot(activeSlide + 1)}
                onGoToPrevious={() => handleClickDot(activeSlide - 1)}
                total={total}
              />
            </GalleryNavigationWrapper>
          )}

          <Grid>
            <div>
              {activeBanner?.title && (
                <Title
                  {...(hasPageTitle && {
                    as: 'h1'
                  })}
                >
                  {activeBanner?.title}
                </Title>
              )}
            </div>

            <div>
              {activeBanner?.description && (
                <Description>
                  {activeBanner?.description}
                </Description>
              )}

              {activeBanner?.linkUrl && activeBanner?.linkLabel && (
                <StyledButton
                  {...!isExternalRoute(activeBanner?.linkUrl) ? {} : {
                    rel: 'noopener',
                    target: '_blank'
                  }}
                  colorTheme={'white'}
                  href={activeBanner?.linkUrl}
                  size={'medium'}
                  variant={'fill'}
                >
                  {activeBanner?.linkLabel}
                </StyledButton>
              )}
            </div>
          </Grid>
        </Wrapper>
      </Section>

      {total > 0 && (
        <Lightbox
          initialSlide={activeSlide}
          isOpen={isOpenLightbox}
          items={map(banners, ({ fileUrl, title, videoUrl }) => ({
            ...videoUrl ? {
              coverImage: fileUrl,
              type: 'video',
              url: videoUrl
            } : {
              type: 'image',
              url: fileUrl
            },
            label: title
          }))}
          onClose={() => setOpenLightbox(false)}
        />
      )}

    </>
  );
};

/**
 * Export `BannerSection` component.
 */

export default BannerSection;
